import { useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogPortal,
	DialogOverlay,
} from "../../../components/ui/dialog";
import { X, BookOpen, Copy, Check, ChevronLeft } from "lucide-react";
import { toast } from "react-toastify";
import { promptExamples } from "../../../shared/promptExamples";

const { ipcRenderer } = window.require("electron");

export const PromptExamplesModal = ({ isOpen, onClose }) => {
	const [selectedExample, setSelectedExample] = useState(null);
	const [copiedId, setCopiedId] = useState(null);

	const handleCopyText = (text, id) => {
		try {
			ipcRenderer.send("copy-logs", text);
			setCopiedId(id);
			toast.success('Prompt copiado com sucesso!', {
				position: "bottom-right",
				autoClose: 2000,
			});
			setTimeout(() => setCopiedId(null), 2000);
		} catch (error) {
			console.error('Erro ao copiar texto:', error);
			toast.error('Erro ao copiar o texto. Tente novamente.', {
				position: "bottom-right",
				autoClose: 3000,
			});
		}
	};

	const handleBack = () => {
		setSelectedExample(null);
	};

	const handleClose = (open) => {
		if (!open) {
			setSelectedExample(null);
			onClose();
		}
	};

	const getCategoryColor = (category) => {
		const colors = {
			'Vendas': 'from-blue-500/20 to-blue-600/20 border-blue-500/30',
			'Beleza': 'from-pink-500/20 to-purple-600/20 border-pink-500/30',
			'Template': 'from-emerald-500/20 to-cyan-600/20 border-emerald-500/30',
			'E-commerce': 'from-orange-500/20 to-red-600/20 border-orange-500/30',
			'Tecnologia': 'from-violet-500/20 to-indigo-600/20 border-violet-500/30',
		};
		return colors[category] || 'from-gray-500/20 to-gray-600/20 border-gray-500/30';
	};

	return (
		<Dialog open={isOpen} onOpenChange={handleClose} modal={true}>
			<DialogPortal>
				<DialogOverlay className="z-[10000] bg-black/85 backdrop-blur-lg" />
				<DialogContent
					className="z-[10001] max-w-[900px] border-modalBorder bg-gradient-to-br from-modalBg via-modalBgSecondary to-modalBg backdrop-blur-3xl shadow-2xl shadow-black/50 max-h-[85vh] overflow-hidden"
					onPointerDownOutside={(e) => e.preventDefault()}
					onInteractOutside={(e) => {
						if (e.target.closest('[role="dialog"]')) {
							e.preventDefault();
						}
					}}
				>
					<div className="absolute inset-0 bg-gradient-to-br from-modalOverlaySoft via-transparent to-modalOverlayStrong opacity-0 hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>
					<div className="absolute inset-0 bg-gradient-to-r from-transparent via-modalOverlayStrong to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1500 pointer-events-none"></div>
					<div className="absolute top-16 left-24 w-40 h-40 bg-effectBlue rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '8s' }}></div>
					<div className="absolute bottom-24 right-20 w-48 h-48 bg-effectPurple rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '9s', animationDelay: '2s' }}></div>
					<div className="absolute top-40 right-40 w-28 h-28 bg-effectIndigo rounded-full blur-2xl animate-pulse pointer-events-none" style={{ animationDuration: '6s', animationDelay: '4s' }}></div>
					<div className="absolute bottom-16 left-16 w-36 h-36 bg-effectCyan rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '7s', animationDelay: '3s' }}></div>

					<button
						onClick={() => handleClose(false)}
						className="absolute right-4 top-4 z-50 flex items-center justify-center w-8 h-8 rounded-full bg-modalOverlayMedium backdrop-blur-xl border border-modalOverlayStrong text-textOnDarkMedium hover:text-white hover:bg-modalOverlayStrong transition-all duration-300 hover:scale-110"
					>
						<X className="w-4 h-4" />
					</button>

					<DialogHeader className="relative z-10">
						<DialogTitle className="flex items-center gap-4 text-white">
							{selectedExample && (
								<button
									onClick={handleBack}
									className="flex items-center justify-center w-10 h-10 rounded-lg bg-modalOverlayMedium hover:bg-modalOverlayStrong transition-all duration-300 hover:scale-105"
								>
									<ChevronLeft className="w-5 h-5" />
								</button>
							)}
							<div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-gray-800/80 via-statusInfo/30 to-purplePrimary/40 shadow-lg border border-white/20">
								<BookOpen className="w-6 h-6 text-statusInfo drop-shadow-sm" />
								<div className="absolute inset-0 bg-gradient-to-br from-statusInfo/10 to-purplePrimary/8 rounded-xl animate-pulse-subtle"></div>
							</div>
							<span className="text-xl font-semibold">
								{selectedExample ? selectedExample.title : 'Exemplos de Prompts'}
							</span>
						</DialogTitle>
					</DialogHeader>

					<div className="relative z-10 mt-6">
						{!selectedExample ? (
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
								{promptExamples.map((example) => (
									<div
										key={example.id}
										onClick={() => setSelectedExample(example)}
										className={`relative p-5 rounded-xl bg-gradient-to-br ${getCategoryColor(example.category)} backdrop-blur-xl border cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-xl group`}
									>
										<div className="flex items-start gap-3 mb-3">
											<span className="text-3xl">{example.title.split(' ')[0]}</span>
											<div className="flex-1">
												<h3 className="text-lg font-semibold text-white group-hover:text-emeraldLight transition-colors">
													{example.title.substring(example.title.indexOf(' ') + 1)}
												</h3>
												<span className="inline-block px-2 py-1 mt-1 text-xs font-medium rounded-full bg-modalOverlayStrong text-textOnDarkStrong">
													{example.category}
												</span>
											</div>
										</div>
										<p className="text-sm text-gray-300 leading-relaxed">
											{example.description}
										</p>
										<div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
											<span className="text-xs text-emeraldLight font-medium">
												Ver exemplo →
											</span>
										</div>
									</div>
								))}
							</div>
						) : (
							<div className="flex flex-col gap-4">
								<div className="flex items-center justify-between gap-4 p-4 rounded-lg bg-modalOverlaySoft border border-modalBorder">
									<div className="flex items-center gap-3">
										<span className="px-3 py-1 text-sm font-medium rounded-full bg-gradient-to-r from-emeraldDark/40 to-emeraldMedium/40 text-emeraldLight border border-emeraldBase/30">
											{selectedExample.category}
										</span>
										<p className="text-sm text-gray-300">{selectedExample.description}</p>
									</div>
									<button
										onClick={() => handleCopyText(selectedExample.content, selectedExample.id)}
										className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-emeraldDark/80 to-emeraldMedium/80 hover:from-emeraldDark hover:to-emeraldMedium text-white font-medium transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-emeraldMedium/40"
									>
										{copiedId === selectedExample.id ? (
											<>
												<Check className="w-4 h-4" />
												<span className="text-sm">Copiado!</span>
											</>
										) : (
											<>
												<Copy className="w-4 h-4" />
												<span className="text-sm">Copiar Texto</span>
											</>
										)}
									</button>
								</div>

								<div className="relative max-h-[55vh] overflow-y-auto rounded-lg bg-modalOverlaySoft backdrop-blur-xl border border-modalBorder p-6 custom-scrollbar">
									<pre className="text-sm text-gray-200 whitespace-pre-wrap font-mono leading-relaxed">
										{selectedExample.content}
									</pre>
								</div>
							</div>
						)}
					</div>

					{!selectedExample && (
						<div className="relative z-10 mt-6 pt-4 border-t border-modalBorder">
							<p className="text-center text-sm text-gray-400">
								Selecione um exemplo para visualizar e copiar o prompt completo
							</p>
						</div>
					)}
				</DialogContent>
			</DialogPortal>
		</Dialog>
	);
};
